/**
 * Breadcrumb Service
 *
 * Tracks the last 20 user actions for crash context.
 * Uses a circular buffer to maintain fixed memory usage.
 *
 * PRIVACY: Target descriptions must be sanitized - no user data, file paths,
 * or sensitive information should ever be included.
 */

import type { Breadcrumb, BreadcrumbAction } from '../types/crashTypes';
import { redactSecretText } from '../../../types/safeConnectionDisplay';

// =============================================================================
// Constants
// =============================================================================

/** Maximum number of breadcrumbs to retain */
const MAX_BREADCRUMBS = 20;

// =============================================================================
// State (Module-level circular buffer)
// =============================================================================

let breadcrumbs: Breadcrumb[] = [];

// =============================================================================
// Public API
// =============================================================================

/**
 * Add a new breadcrumb to the trail.
 * Automatically timestamps and maintains circular buffer of last 20 actions.
 *
 * @param action - Type of action (click, navigation, etc.)
 * @param target - Sanitized description of the action target
 */
export function addBreadcrumb(action: BreadcrumbAction, target: string): void {
  const breadcrumb: Breadcrumb = {
    timestamp: Date.now(),
    action,
    // Categorical by contract; masked anyway so a careless target can never carry a credential (security slice).
    target: redactSecretText(target),
  };

  // Add to end of array
  breadcrumbs.push(breadcrumb);

  // Remove oldest if exceeding max (circular buffer behavior)
  if (breadcrumbs.length > MAX_BREADCRUMBS) {
    breadcrumbs.shift();
  }
}

/**
 * Get all current breadcrumbs, oldest first.
 *
 * @returns Copy of the breadcrumb array
 */
export function getBreadcrumbs(): Breadcrumb[] {
  // Return a copy to prevent external mutation
  return [...breadcrumbs];
}

/**
 * Clear all breadcrumbs.
 * Useful for testing or when starting a new session.
 */
export function clearBreadcrumbs(): void {
  breadcrumbs = [];
}

/**
 * Get the current number of breadcrumbs.
 * Useful for testing.
 */
export function getBreadcrumbCount(): number {
  return breadcrumbs.length;
}
